import {
  ApiError,
  type CustomerDuplicateRecord,
  formatUsPhone,
  type MergeCustomersResult,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { CustomerBackupButton } from './customer-backup-button.js'

function activityCount(r: CustomerDuplicateRecord): number {
  return r.orderCount + r.repairTicketCount + r.supportTicketCount + r.subscriptionCount
}

export function MergeCustomersDialog({
  a,
  b,
  onClose,
  onMerged,
}: {
  a: CustomerDuplicateRecord
  b: CustomerDuplicateRecord
  onClose: () => void
  /** Called after a successful merge, instead of `onClose`. */
  onMerged?: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [winnerId, setWinnerId] = useState(() =>
    activityCount(a) !== activityCount(b)
      ? activityCount(a) > activityCount(b)
        ? a.id
        : b.id
      : a.createdAt <= b.createdAt
        ? a.id
        : b.id,
  )
  const loser = winnerId === a.id ? b : a
  const bothHaveStripe = !!a.stripeCustomerId && !!b.stripeCustomerId

  const merge = useMutation({
    mutationFn: () =>
      api.post<MergeCustomersResult>('/customers/merge', { winnerId, loserId: loser.id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      toast.success('Customers merged')
      if (onMerged) onMerged()
      else onClose()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not merge these customers',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Merge these two customers</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            Choose which record to keep. Orders, repair tickets, support tickets, subscriptions,
            extra phone numbers and saved cards on the other one move onto it. A merge can be undone
            afterwards from Merge history on the Duplicates &amp; cleanup page.
          </p>
          <CustomerBackupButton />
          {[a, b].map((r) => (
            <label
              key={r.id}
              className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm has-[:checked]:border-primary"
            >
              <input
                type="radio"
                name="winner"
                className="mt-1"
                checked={winnerId === r.id}
                onChange={() => setWinnerId(r.id)}
              />
              <div>
                <p className="font-medium">{r.name}</p>
                {r.businessName ? (
                  <p className="text-xs text-foreground-muted">{r.businessName}</p>
                ) : null}
                {r.phone ? (
                  <p className="text-xs text-foreground-muted">{formatUsPhone(r.phone)}</p>
                ) : null}
                {r.email ? <p className="text-xs text-foreground-muted">{r.email}</p> : null}
                <p className="text-xs text-foreground-muted">
                  {r.orderCount} orders, {r.repairTicketCount} repairs, {r.supportTicketCount}{' '}
                  support tickets, {r.subscriptionCount} subscriptions
                </p>
              </div>
            </label>
          ))}
          {bothHaveStripe ? (
            <p className="flex items-start gap-1.5 text-xs text-warning">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              Both records have Stripe billing set up. If the one being removed has saved cards or
              an active subscription, this merge will be refused — keep that one instead, or remove
              them first.
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={merge.isPending} onClick={() => merge.mutate()}>
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
